import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { api } from '../services/api';
import { useCompanyId } from './CompanyContext';

interface OnboardingStatus {
  completed: boolean;
  currentStep?: number;
  completedAt?: string;
  skipped?: boolean;
}

interface OnboardingContextValue {
  isOnboardingComplete: boolean;
  completeOnboarding: () => Promise<void>;
  skipOnboarding: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function useOnboarding(): OnboardingContextValue {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}

interface OnboardingProviderProps {
  children: ReactNode;
  initialStatus?: OnboardingStatus;
}

export function OnboardingProvider({ children, initialStatus }: OnboardingProviderProps) {
  const companyId = useCompanyId();
  const [status, setStatus] = useState<OnboardingStatus>(
    initialStatus || { completed: false, currentStep: 1 }
  );

  const completeOnboarding = useCallback(async () => {
    setStatus({ completed: true });
    try {
      await api.updateOnboardingStatus(companyId, { completed: true });
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  }, [companyId]);

  const skipOnboarding = useCallback(async () => {
    setStatus({ completed: true, skipped: true });
    try {
      await api.updateOnboardingStatus(companyId, { skipped: true, completed: true });
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
    }
  }, [companyId]);

  return (
    <OnboardingContext.Provider
      value={{
        isOnboardingComplete: status.completed,
        completeOnboarding,
        skipOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}
